import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { FiUser, FiSettings, FiLogOut, FiAlertCircle } from 'react-icons/fi';
import { logout } from '../../redux/slices/authSlice';
import '../../styles/homeDelvante.css';

const ProfileLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const menuItems = [
    { path: '/profile', icon: FiUser, label: 'Profile' },
    { path: '/profile/settings', icon: FiSettings, label: 'Settings' },
  ];

  const handleLogout = async () => {
    setShowLogoutConfirm(false);
    try {
      await dispatch(logout()).unwrap();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/login', { replace: true });
    }
  };

  const currentPath = location.pathname;

  return (
    <div className="home-delvante" style={{ background: 'var(--cream-pale)', minHeight: '100vh', paddingTop: '4.6rem', fontFamily: "'Jost',sans-serif" }}>
      <style>{`
        .pl-bar{display:flex;align-items:center;justify-content:center;gap:3rem;
          background:var(--white);border-bottom:1px solid rgba(13,30,46,0.08);padding:0.85rem 3rem;
          position:relative;z-index:5}
        .pl-bar-link{position:relative;display:inline-flex;align-items:center;gap:0.45rem;text-decoration:none;
          font-size:0.68rem;font-weight:500;letter-spacing:0.2em;text-transform:uppercase;color:var(--text-mid);
          padding:0.25rem 0;transition:color 0.3s;background:none;border:none;cursor:pointer;font-family:'Jost',sans-serif}
        .pl-bar-link::after{content:'';position:absolute;bottom:-1px;left:0;right:0;height:2px;background:var(--gold);
          transform:scaleX(0);transform-origin:left;transition:transform 0.35s ease}
        .pl-bar-link:hover{color:var(--navy)}
        .pl-bar-link-active{color:var(--navy)}
        .pl-bar-link-active::after{transform:scaleX(1)}
        .pl-bar-logout{color:#b91c1c!important}
        .pl-bar-logout:hover{color:#991b1b!important}
        @media(max-width:640px){.pl-bar{gap:1.5rem;padding:0.85rem 1.5rem!important}.pl-bar-link{font-size:0.6rem;letter-spacing:0.14em}}
      `}</style>

      {/* Thin secondary bar below the main navigation */}
      <nav className="pl-bar">
        {menuItems.map((item) => {
          const isActive = currentPath === item.path;
          const Icon = item.icon;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`pl-bar-link ${isActive ? 'pl-bar-link-active' : ''}`}
            >
              <Icon style={{ width: 13, height: 13 }} />
              {item.label}
            </Link>
          );
        })}
        <button onClick={() => setShowLogoutConfirm(true)} className="pl-bar-link pl-bar-logout">
          <FiLogOut style={{ width: 13, height: 13 }} />
          Log Out
        </button>
      </nav>

      <motion.div
        key={currentPath}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 220, damping: 26 }}
      >
        {children}
      </motion.div>

      {/* Logout confirmation */}
      <AnimatePresence>
        {showLogoutConfirm && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowLogoutConfirm(false)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(4,8,18,0.7)', zIndex: 90 }}
            />
            <div style={{ position: 'fixed', inset: 0, zIndex: 95, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
              <motion.div
                initial={{ opacity: 0, scale: 0.94, y: 12 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.94, y: 12 }}
                transition={{ type: 'spring', stiffness: 300, damping: 26 }}
                style={{
                  background: 'var(--white)', borderRadius: '4px', maxWidth: 400, width: '100%', padding: '2.6rem 2.4rem',
                  border: '1px solid var(--border)', boxShadow: '0 24px 64px rgba(13,30,46,0.25)'
                }}
              >
                <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'var(--cream-pale)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.4rem' }}>
                  <FiAlertCircle style={{ width: 26, height: 26, color: 'var(--navy)' }} />
                </div>
                <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '1.7rem', fontWeight: 600, color: 'var(--navy)', textAlign: 'center', margin: '0 0 0.5rem' }}>
                  Log Out?
                </h2>
                <p style={{ fontSize: '0.9rem', fontWeight: 300, color: 'var(--text-mid)', textAlign: 'center', margin: '0 0 1.8rem' }}>
                  You are about to log out of your account.
                </p>
                <div style={{ display: 'flex', gap: '0.8rem' }}>
                  <button
                    onClick={() => setShowLogoutConfirm(false)}
                    style={{ flex: 1, padding: '0.8rem', borderRadius: '2px', fontSize: '0.7rem', fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', background: 'none', border: '1px solid var(--border)', color: 'var(--text-mid)', cursor: 'pointer', fontFamily: "'Jost',sans-serif" }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleLogout}
                    style={{ flex: 1, padding: '0.8rem', borderRadius: '2px', fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', background: 'var(--navy)', border: 'none', color: 'var(--white)', cursor: 'pointer', fontFamily: "'Jost',sans-serif" }}
                  >
                    Log Out
                  </button>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProfileLayout;
